// --- Geometry: node addressing + pattern matching -------------------------
import { rotateCW, cubeKey } from "../coords/cube.js";
/**
 * A node's "vector": the sum of its three hex cube coords. Unique per node and
 * linear under rotation, so relative offsets between nodes rotate by `rotateCW`.
 */
export function nodeVector(node) {
    const [a, b, c] = node.hexes;
    return { q: a.q + b.q + c.q, r: a.r + b.r + c.r, s: a.s + b.s + c.s };
}
/** Reflection across the q-axis (swaps r and s); preserves q+r+s = 0. */
function mirror(c) {
    return { q: c.q, r: c.s, s: c.r };
}
function rotateN(c, times) {
    let out = c;
    for (let i = 0; i < times; i++)
        out = rotateCW(out);
    return out;
}
/**
 * The 12 orientations of the board's dihedral symmetry (6 rotations, each
 * optionally mirrored), as transforms on node-vector offsets.
 */
const ORIENTATIONS = [
    ...Array.from({ length: 6 }, (_, k) => (c) => rotateN(c, k)),
    ...Array.from({ length: 6 }, (_, k) => (c) => rotateN(mirror(c), k)),
];
function addVec(a, b) {
    return { q: a.q + b.q, r: a.r + b.r, s: a.s + b.s };
}
/**
 * Is a pattern's reflection a genuinely different layout from every rotation of
 * itself? (i.e. is the pattern *chiral*.)
 *
 * The board's symmetry is dihedral — 6 rotations, each optionally mirrored — and
 * a Fortress may be built in any of those 12 orientations. When a pattern is
 * chiral its mirror image is a layout you can't reach by rotating, so both build
 * orientations are worth showing. When it's achiral the "mirror" is just one of
 * the rotations already allowed (e.g. two buildings related by a half-turn:
 * mirroring and rotating 180° give the same thing), so drawing it adds nothing.
 *
 * Returns true iff no rotation of the mirrored pattern reproduces the original,
 * matching building *shapes* (a mirror that swaps two different shapes is not a
 * symmetry), marker positions and bridge edges, up to translation.
 */
export function isBuildingPatternAsymmetric(pattern) {
    // Compare in DOUBLED coordinates so a bridge reduces to one point (the sum
    // of its endpoints = 2× its midpoint) that translates/rotates consistently
    // with the doubled node points.
    const dbl = (c) => ({ q: 2 * c.q, r: 2 * c.r, s: 2 * c.s });
    const transformedPoints = (orient) => [
        ...pattern.buildings.map((b) => ({ label: b.shape, v: dbl(orient(b.offset)) })),
        // Markers carry no shape, so tag them apart from any building shape.
        ...pattern.markers.map((m) => ({ label: " marker", v: dbl(orient(m)) })),
        ...pattern.bridges.map(([a, b]) => ({
            label: " bridge",
            v: addVec(orient(a), orient(b)),
        })),
    ];
    // A signature that's equal for two point sets iff they coincide up to a
    // translation: re-origin on the lexicographically-smallest point (which moves
    // with the set under translation), then sort the labelled offsets.
    const signature = (pts) => {
        if (pts.length === 0)
            return "";
        const lexLess = (a, b) => a.q !== b.q ? a.q < b.q : a.r !== b.r ? a.r < b.r : a.s < b.s;
        let origin = pts[0].v;
        for (const p of pts)
            if (lexLess(p.v, origin))
                origin = p.v;
        return pts
            .map((p) => `${p.label}@${p.v.q - origin.q},${p.v.r - origin.r},${p.v.s - origin.s}`)
            .sort()
            .join("|");
    };
    const original = signature(transformedPoints((c) => c));
    // Rotations form a cyclic group, so it's enough to test the 6 mirrored
    // orientations against the (unrotated) original — if any matches, the mirror
    // is reachable by rotation alone and the pattern is achiral.
    for (let k = 0; k < 6; k++) {
        if (signature(transformedPoints((c) => rotateN(mirror(c), k))) === original) {
            return false;
        }
    }
    return true;
}
/** The shared walk behind both matchers: every (anchor, orientation) placement
 * of `pattern` that satisfies the constraints, in board/orientation order. */
function* iterateFortressMatches(board, buildingAt, pattern, opts) {
    const { allowReflection = true, restrictTo, anchorNodeId, orientationIndex, bridgeAt, markerBlockedAt, acceptMatch, } = opts;
    if (pattern.bridges.length > 0 && !bridgeAt)
        return;
    const orientations = allowReflection
        ? ORIENTATIONS
        : ORIENTATIONS.slice(0, 6);
    // node-vector key -> node id, so an offset can be resolved to a board node.
    const idByVector = new Map();
    for (const node of board.nodes) {
        idByVector.set(cubeKey(nodeVector(node)), node.id);
    }
    // A pattern needs at least one placeable requirement to anchor on: some cards
    // (e.g. Hole Worm, Artifact of Atrocity) consist solely of bridges.
    const cells = pattern.buildings;
    if (cells.length === 0 && pattern.bridges.length === 0)
        return;
    for (const anchor of board.nodes) {
        if (anchorNodeId !== undefined && anchor.id !== anchorNodeId)
            continue;
        const anchorVec = nodeVector(anchor);
        for (let oi = 0; oi < orientations.length; oi++) {
            if (orientationIndex !== undefined && oi !== orientationIndex)
                continue;
            const orient = orientations[oi];
            const buildingNodeIds = [];
            const used = new Set();
            let ok = true;
            for (const cell of cells) {
                const id = idByVector.get(cubeKey(addVec(anchorVec, orient(cell.offset))));
                if (id === undefined || used.has(id)) {
                    ok = false;
                    break;
                }
                if (restrictTo && !restrictTo.has(id)) {
                    ok = false;
                    break;
                }
                const have = buildingAt(id);
                if (have === null) {
                    ok = false;
                    break;
                }
                if (cell.shape !== "wild" && cell.shape !== have) {
                    ok = false;
                    break;
                }
                used.add(id);
                buildingNodeIds.push(id);
            }
            if (!ok)
                continue;
            // Markers (the X's) need their node to EXIST and to pass the caller's
            // `markerBlockedAt` veto (another player's fortress-hardened node can't
            // be taken). Plain occupancy doesn't block the match: the implementer's
            // own building on an X survives; another player's unhardened building is
            // demolished when the fortress is implemented (a server side effect).
            const markerNodeIds = [];
            for (const m of pattern.markers) {
                const id = idByVector.get(cubeKey(addVec(anchorVec, orient(m))));
                if (id === undefined || markerBlockedAt?.(id)) {
                    ok = false;
                    break;
                }
                markerNodeIds.push(id);
            }
            if (!ok)
                continue;
            const bridgeNodeIdPairs = [];
            for (const [a, b] of pattern.bridges) {
                const aId = idByVector.get(cubeKey(addVec(anchorVec, orient(a))));
                const bId = idByVector.get(cubeKey(addVec(anchorVec, orient(b))));
                if (aId === undefined || bId === undefined || !bridgeAt(aId, bId)) {
                    ok = false;
                    break;
                }
                bridgeNodeIdPairs.push([aId, bId]);
            }
            if (!ok)
                continue;
            const match = {
                buildingNodeIds,
                markerNodeIds,
                bridgeNodeIdPairs,
                anchorNodeId: anchor.id,
                orientation: oi,
            };
            if (acceptMatch && !acceptMatch(match))
                continue;
            yield match;
        }
    }
}
/**
 * Find a placement of `pattern` on the board where every building cell sits on
 * a node carrying the required building (per `buildingAt`, which returns a
 * player's building at a node, or null), in some orientation. Returns the
 * matched building + marker node ids (+ bridge endpoint pairs), or null if it
 * can't be realised.
 *
 * `allowReflection` defaults to true (a Fortress may be built mirrored). Pass a
 * `restrictTo` set to require the buildings come from a specific candidate set
 * (e.g. the nodes a player selected) rather than searching the whole board.
 * `anchorNodeId` + `orientationIndex` pin the placement exactly — the way a
 * server reproduces the placement a client chose out of
 * `matchAllFortressPatterns`.
 *
 * Patterns with bridges additionally require `bridgeAt` (does a Bridge exist on
 * the edge between two adjacent node ids?); without it they never match, so a
 * caller can't silently implement a bridge Fortress with no bridges.
 *
 * `markerBlockedAt` vetoes marker (X) placement per node — callers use it to
 * block nodes hardened by ANOTHER player's fortress, and nodes holding an
 * opponent's protected (indestructible) building — neither can be demolished.
 */
export function matchFortressPattern(board, buildingAt, pattern, opts = {}) {
    for (const match of iterateFortressMatches(board, buildingAt, pattern, opts)) {
        return match;
    }
    return null;
}
/**
 * Every distinct placement of `pattern` the constraints allow — the candidate
 * set a player picks a location (and orientation) from. Same options as
 * `matchFortressPattern`. Placements that realise the exact same board layout
 * (equal building/marker node sets and bridge edges) via different
 * (anchor, orientation) pairs are deduplicated, keeping the first.
 */
export function matchAllFortressPatterns(board, buildingAt, pattern, opts = {}) {
    const out = [];
    const seen = new Set();
    for (const match of iterateFortressMatches(board, buildingAt, pattern, opts)) {
        const signature = [
            [...match.buildingNodeIds].sort((a, b) => a - b).join(","),
            [...match.markerNodeIds].sort((a, b) => a - b).join(","),
            match.bridgeNodeIdPairs
                .map(([a, b]) => (a < b ? `${a}-${b}` : `${b}-${a}`))
                .sort()
                .join(","),
        ].join("|");
        if (seen.has(signature))
            continue;
        seen.add(signature);
        out.push(match);
    }
    return out;
}
/**
 * Node ids already committed to implemented fortresses (their buildings AND
 * markers). A building on one of these can't be reused to realise another
 * fortress, so callers fold this into their `buildingAt` (return null for a
 * committed node). Generic over the stored card shape, so the server schema
 * (`FortressCardState`) and the client view both fit.
 */
export function committedFortressNodes(fortresses) {
    const used = new Set();
    for (const f of fortresses)
        for (const id of f.nodeIds)
            used.add(id);
    return used;
}
/** Index a fortress-card catalogue (match snapshot or code defaults) by id. */
export function fortressCardsById(cards) {
    const map = new Map();
    for (const c of cards.regularCards)
        map.set(c.id, c);
    for (const c of cards.winCards)
        map.set(c.id, c);
    return map;
}
/** Index a fortress-card catalogue by display name (for history / UI labels). */
export function fortressCardsByName(cards) {
    const map = new Map();
    for (const c of [...cards.regularCards, ...cards.winCards]) {
        map.set(c.name, c);
    }
    return map;
}
//# sourceMappingURL=fortress.js.map